import { 
  InterventionInput, 
  FertilizerInput, 
  StabilizedFertilizerInput, 
  ControlledReleaseFertilizerInput,
  InputType,
  InputData,
  FuelInput,
  CustomerCodes
} from '../types';

/**
 * Helper function to check if an organization type is "Customer" (case-insensitive)
 */
export const isCustomerOrganization = (orgType: string | string[]): boolean => {
  if (!orgType) return false;
  
  // Handle both string and array of strings
  const types = Array.isArray(orgType) ? orgType : [orgType];
  
  return types.some(type => 
    type && type.toLowerCase().trim() === 'customer'
  );
};


export interface CustomerCodeMapping {
  customer_org_id: string;
  input_id: string;
  codes: CustomerCodes;
}

export interface MatchingResult {
  success: boolean;
  catalogInput?: InterventionInput;
  error?: string;
  warnings?: string[];
}

export interface MatchingOptions {
  customerOrgId?: string;
}


/**
 * Centralized service for matching customer codes to input catalog entries
 */
export class InputMatchingService {
  private inputMap: Map<string, InterventionInput> = new Map();
  private customerMappings: Map<string, CustomerCodeMapping[]> = new Map();

  constructor(inputs: InterventionInput[], mappings: CustomerCodeMapping[] = []) {
    this.buildInputMap(inputs);
    this.buildCustomerMappings(mappings);
  }

  /**
   * Build internal input map for fast lookup
   */
  private buildInputMap(inputs: InterventionInput[]): void {
    this.inputMap.clear();
    inputs.forEach(input => {
      // Map by input_code
      this.inputMap.set(input.input_code, input);
      // Map by lowercase input_code for case-insensitive lookups
      this.inputMap.set(input.input_code.toLowerCase(), input);
      // Map by _id to support mappings that reference database id
      if (input._id) {
        this.inputMap.set(input._id, input);
      }
    });
  }

  /**
   * Build customer code mappings for reverse lookup
   */
  private buildCustomerMappings(mappings: CustomerCodeMapping[]): void {
    this.customerMappings.clear();
    mappings.forEach(mapping => {
      const inputCode = mapping.codes?.input_code?.toLowerCase();
      if (!inputCode) {
        // If no input code, skip indexing
        return;
      }
      const key = `${mapping.customer_org_id}:${inputCode}`;
      if (!this.customerMappings.has(key)) {
        this.customerMappings.set(key, []);
      }
      this.customerMappings.get(key)!.push(mapping);
    });
  }

  /**
   * Match customer codes to a catalog entry using flexible code matching
   */
  matchInput(
    codeInput: CustomerCodes, 
    options: MatchingOptions = {}
  ): MatchingResult {
    const { customerOrgId } = options;

    // Enforce required context
    if (!customerOrgId) {
      return { success: false, error: 'customerOrgId is required for matching' };
    }

    // Enforce required input_code
    if (!codeInput?.input_code) {
      return { success: false, error: 'input_code is required in customer codes' };
    }

    // Strict, org-scoped, exact matching only (no fallback)
    const exactResult = this.matchExact(codeInput, customerOrgId);
    return exactResult;
  }

  /**
   * Exact matching - all provided codes must match exactly
   */
  private matchExact(codes: CustomerCodes, customerOrgId: string): MatchingResult {
    const key = `${customerOrgId}:${codes.input_code.toLowerCase()}`;
    const orgMappings = this.customerMappings.get(key) || [];


    console.log('orgMappings', JSON.stringify(orgMappings, null, 2));
    const matches = orgMappings.filter(mapping => this.codesMatchStrict(codes, mapping.codes));

    if (matches.length === 0) {
      console.log('no matches for codes', JSON.stringify(codes, null, 2));
      return { success: false, error: `No mapping found for org '${customerOrgId}' and codes ${JSON.stringify(codes)}` };
    }

    console.log('matches', JSON.stringify(matches, null, 2));
    if (matches.length > 1) {
      // Pick the most specific mapping: highest count of defined non-empty code fields (excluding input_code)
      const score = (mc: CustomerCodes) => {
        let s = 0;
        if (mc?.inhibitor && mc.inhibitor !== '') s++;
        if (mc?.crop && mc.crop !== '') s++;
        if (mc?.soil && mc.soil !== '') s++;
        return s;
      };
      const scored = matches.map(m => ({ m, s: score(m.codes) }));
      const max = Math.max(...scored.map(x => x.s));
      const top = scored.filter(x => x.s === max);
      if (top.length > 1) {
        return { success: false, error: `Ambiguous mapping: multiple equally specific mappings match for org '${customerOrgId}' and codes ${JSON.stringify(codes)}` };
      }
      const selected = top[0].m;
      const catalogInput = this.inputMap.get(selected.input_id);
      if (!catalogInput) {
        return { success: false, error: `Mapping found but catalog input not available for input_id '${selected.input_id}'` };
      }
      return { success: true, catalogInput };
    }

    const selected = matches[0];
    const catalogInput = this.inputMap.get(selected.input_id);
    if (!catalogInput) {
      return { success: false, error: `Mapping found but catalog input not available for input_id '${selected.input_id}'` };
    }
    return { success: true, catalogInput };
  }

  /**
   * Check that all provided codes match (case-insensitive). Unprovided codes are ignored.
   */
  private codesMatchStrict(inputCodes: CustomerCodes, mappingCodes: CustomerCodes): boolean {
    const eq = (a?: string, b?: string) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

    // input_code is always required in both mapping and input
    if (!mappingCodes?.input_code || !inputCodes?.input_code) return false;
    if (!eq(inputCodes.input_code, mappingCodes.input_code)) return false;

    // For any additional fields defined in mapping (non-empty), they MUST be present and equal in input
    const mappingKeys = Object.keys(mappingCodes) as (keyof CustomerCodes)[];
    for (const key of mappingKeys) {
      if (key === 'input_code') continue;
      const mVal = (mappingCodes as any)[key];
      if (mVal === undefined || mVal === '') continue; // not constrained by mapping
      const iVal = (inputCodes as any)[key];
      if (iVal === undefined || iVal === '') return false; // mapping requires this code, but input doesn't provide it
      if (!eq(iVal, mVal)) return false; // provided but does not match
    }

    return true;
  }


  /**
   * Transform customer input to FertilizerInput using matching
   */
  transformCustomerInput(
    customerInput: {
      codes: CustomerCodes;
      rate?: number;
      methodologyId?: string;
      inhibitor_rate?: number;
      crf_rate?: number;
    },
    assetId: string,
    periodYear: number,
    options: MatchingOptions = {},
    isRealized: boolean = false
  ): InputData | FertilizerInput | StabilizedFertilizerInput | ControlledReleaseFertilizerInput {
    const { codes, rate, methodologyId, inhibitor_rate, crf_rate } = customerInput;
    
    const matchingResult = this.matchInput(codes, options);

    if (!matchingResult.success) {
      throw new Error(matchingResult.error || 'Input matching failed');
    }

    const catalogInput = matchingResult.catalogInput!;
    
    const base: FertilizerInput | FuelInput | StabilizedFertilizerInput | ControlledReleaseFertilizerInput = {
      code: catalogInput.input_code,
      name: catalogInput.input_name,
      type: catalogInput.input_type,
      external_asset_id: assetId,
      product_name: catalogInput.input_name,
      yield_period: periodYear.toString(),
      application_rate: rate || 0,
      emission_data: {
        nitrogen_content: catalogInput.nitrogen_content,
        emission_factor: catalogInput.emission_factor,
        methodology_code: methodologyId,
      },
      is_realized: isRealized,
    };

    // Single switch by input type
    switch (catalogInput.input_type) {
      case 'fuel': {
        return {
          ...base,
          type: 'fuel',
          fuel_type: (catalogInput as any).fuel_type || catalogInput.input_name,
        } as FuelInput;
      }
      case 'nitrogen_stabilized_fertilizer': {
        const baseInput: FertilizerInput = {
          ...base,
          type: 'nitrogen_stabilized_fertilizer',
        } as FertilizerInput;
        return {
          ...baseInput,
          stabilizer_type: (catalogInput as any).stabilizer_type || 'Unknown Stabilizer',
          stabilizer_application_rate: inhibitor_rate || 0
        } as StabilizedFertilizerInput;
      }
      case 'controlled_release_fertilizer': {
        const baseInput: FertilizerInput = {
          ...base,
          type: 'controlled_release_fertilizer',
        };
        return {
          ...baseInput,
          crf_blend_percent: (catalogInput as any).crf_blend_percent || 0,
          crf_application_rate: crf_rate || 0
        } as ControlledReleaseFertilizerInput;
      }
      case 'conventional_fertilizer':
      case 'low_carbon_fertilizer': {
        return {
          ...base,
          type: catalogInput.input_type,
        };
      }
      default:
        return { ...base, type: catalogInput.input_type };
    }
  }

  /**
   * Update input catalog and rebuild mappings
   */
  updateInputs(inputs: InterventionInput[]): void {
    this.buildInputMap(inputs);
  }

  /**
   * Update customer mappings and rebuild index
   */
  updateMappings(mappings: CustomerCodeMapping[]): void {
    this.buildCustomerMappings(mappings);
  }

  /**
   * Get all customer codes for a specific input
   */
  getCustomerCodesForInput(inputId: string): CustomerCodeMapping[] {
    return Array.from(this.customerMappings.values())
      .flat()
      .filter(mapping => mapping.input_id === inputId);
  }

  /**
   * Get all inputs for a customer organization
   */
  getInputsForCustomer(customerOrgId: string): InterventionInput[] {
    const inputIds = Array.from(this.customerMappings.values())
      .flat()
      .filter(mapping => mapping.customer_org_id === customerOrgId)
      .map(mapping => mapping.input_id);

    return inputIds
      .map(id => this.inputMap.get(id))
      .filter((input): input is InterventionInput => input !== undefined);
  }
}

/**
 * Factory function to create InputMatchingService instance
 */
export function createInputMatchingService(
  inputs: InterventionInput[], 
  mappings: CustomerCodeMapping[] = []
): InputMatchingService {
  return new InputMatchingService(inputs, mappings);
}
